/**
 * Seam between the shifts API and the local persisted store.
 *
 * - true (default): useScheduleQuery hydrates the store from
 *   `/restaurants/{restaurantId}/shifts` and the Save button pushes the visible
 *   week back with one PUT. The backend now implements both.
 * - false: the schedule runs entirely off the local persisted zustand store, no
 *   network calls, and the Save button hides itself, so rolling the endpoint
 *   back degrades the feature instead of breaking the page.
 *
 * Components are identical in both modes: they read the store and call the save
 * hook, both of which honour this flag. Same arrangement as the floor planner.
 *
 * Two properties of the endpoint that shape the code around it:
 *  - a week is one document, addressed by its Monday, and a PUT REPLACES it -
 *    shifts absent from the payload are deleted;
 *  - employee colours are restaurant-wide rather than week-scoped, so an absent
 *    `colors` list means "leave them alone", never "clear them".
 */
export const SCHEDULE_USE_API = true

/** Backend path, relative to `/restaurants/{restaurantId}`. */
export const SCHEDULE_ENDPOINT = '/shifts'

/**
 * Entry types on the roster, and what the backend has to add for them.
 *
 * The front end is complete and already sends `type` on every shift. NestJS is
 * configured with `whitelist: true`, so until the column exists the field is
 * stripped on the way in and every entry comes back as `WORK`: the calendar
 * looks right until a reload, then absences turn into work shifts. That is why
 * this is worth doing on the backend rather than leaving indefinitely.
 *
 * What it takes, in `menu_core_be`:
 *
 * 1. A Postgres enum and a column on `shifts`, defaulting to the old
 *    behaviour so existing rows need no migration of their own:
 *
 *      CREATE TYPE shift_type AS ENUM (
 *        'WORK', 'ON_CALL', 'TRAINING',
 *        'TIME_OFF', 'VACATION', 'SICK_LEAVE', 'UNAVAILABLE'
 *      );
 *      ALTER TABLE shifts
 *        ADD COLUMN type shift_type NOT NULL DEFAULT 'WORK';
 *
 * 2. The matching field on the entity, beside `note` in
 *    `src/domain/shifts/entities/shift.entity.ts`:
 *
 *      @Column({ type: 'enum', enum: ShiftTypeEnum, default: ShiftTypeEnum.WORK })
 *      type: ShiftTypeEnum;
 *
 * 3. `ShiftDto` and `UpsertShiftDto` gain it. On the upsert DTO it must be
 *    optional with the same default, so a client that predates the field still
 *    writes a valid week:
 *
 *      @Expose()
 *      @IsOptional()
 *      @IsEnum(ShiftTypeEnum)
 *      readonly type?: ShiftTypeEnum = ShiftTypeEnum.WORK;
 *
 * 4. Nothing else. Times keep their meaning: an all-day entry arrives as
 *    0-1440 like any other, so no validator, index or overlap rule changes.
 *
 * The one product decision the backend should NOT make on its own: whether an
 * absence may overlap a work shift for the same person. Today the front end
 * refuses any overlap regardless of type, which is the stricter reading and the
 * one to keep unless someone asks otherwise.
 */
export const SHIFT_TYPES_BACKEND_READY = false
